import { useApiClient } from '../../api/useApiClient';

/**
 * Servicio para gestión de tipos de documentos (series documentales) y sus especificaciones de metadata
 * Encapsula todas las operaciones relacionadas con document types y metadata specs
 */
class SeriesService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    // ==================== DOCUMENT TYPE (SERIES) CRUD ====================

    /**
     * Obtener tipos de documentos disponibles
     * @returns {Promise} Response con lista de tipos de documentos
     */
    /**
     * Lista series documentales visibles para el usuario autenticado.
     *
     * @param {{ scope?: 'all', q?: string }} [options]
     *   scope='all' → requiere ADMIN o IMPORTER; devuelve TODAS las series (uso:
     *     pantalla de gestión de series documentales, bot/CLI de import masivo).
     *   scope undefined → ACL estricto por grupos del usuario (uso: Control de
     *     Calidad, flujos por usuario final). Cumple criterio de seguridad
     *     "las series listadas son las que el usuario puede acceder según ACLs".
     *   q → fragmento para filtrar por nombre (case/accent-insensitive).
     */
    async getTiposDocumentos(options = {}) {
        const params = new URLSearchParams();
        if (options.scope) params.set('scope', options.scope);
        if (options.q) params.set('q', options.q);
        const qs = params.toString();
        return this.apiClient.get(qs ? `/api/v1/series?${qs}` : '/api/v1/series');
    }

    async getFolders() {
        return this.apiClient.get('/api/v1/admin/folders');
    }

    // ─── CRUD de folders (#57) ──────────────────────────────────────────────
    // Folders comparten tabla con series pero tienen endpoints propios bajo
    // /api/v1/admin/folders. ID validado con misma política que series.

    /**
     * Crear un folder nuevo.
     * @param {{id: string, name: string, parent: ?string}} folder
     */
    async createFolder(folder) {
        return this.apiClient.post('/api/v1/admin/folders', folder);
    }

    /**
     * Renombrar un folder existente (PUT solo cambia el nombre).
     * Para mover, usar `moveSerie(id, newParent)` — el endpoint es agnóstico al tipo.
     */
    async renameFolder(id, name) {
        return this.apiClient.put(`/api/v1/admin/folders/${id}`, { name });
    }

    /**
     * Mover un folder bajo otro padre (o null = raíz). Reutiliza el endpoint
     * de move de series — sirve para folders también (service agnóstico al tipo).
     * Dispara reindex en cascada async.
     */
    async moveFolder(id, newParentId) {
        return this.apiClient.post(`/api/v1/series/${id}/move`, { parentOverride: newParentId });
    }

    /**
     * Eliminar un folder. Falla con 400 si tiene hijos directos.
     */
    async deleteFolder(id) {
        return this.apiClient.delete(`/api/v1/admin/folders/${id}`);
    }

    /**
     * Obtener los datos completos de un tipo de documento
     * @param {string} id - ID del tipo de documento
     * @returns {Promise} Response con los datos completos del tipo
     */
    async getTipoDocumento(id) {
        return this.apiClient.get(`/api/v1/series/${id}`);
    }

    /**
     * Verificar si existe un tipo de documento
     * @param {string} id - ID del tipo de documento a verificar
     * @returns {Promise} Response con {exists: boolean}
     */
    async checkExists(id) {
        return this.apiClient.head(`/api/v1/series/${id}`);
    }

    /**
     * Crear un nuevo tipo de documento (serie documental)
     * @param {Object} tipoDoc - Datos del tipo de documento
     * @param {string} tipoDoc.id - Identificador único
     * @param {string} tipoDoc.descripcion - Descripción del tipo
     * @param {string} tipoDoc.padre - ID del nodo padre
     * @param {number} tipoDoc.orden - Orden de visualización
     * @param {boolean} tipoDoc.ocrRequired - Requiere OCR
     * @param {boolean} tipoDoc.reviewRequired - Requiere revisión
     * @param {boolean} tipoDoc.approvalRequired - Requiere aprobación
     * @param {boolean} tipoDoc.signatureRequired - Requiere firma
     * @returns {Promise} Response con resultado de la creación
     */
    async createTipoDocumento(tipoDoc) {
        return this.apiClient.post('/api/v1/series', tipoDoc);
    }

    /**
     * Actualizar un tipo de documento existente
     * @param {string} id - ID del tipo de documento a actualizar
     * @param {Object} tipoDoc - Datos actualizados
     * @returns {Promise} Response con resultado de la actualización
     */
    async updateTipoDocumento(id, tipoDoc) {
        return this.apiClient.put(`/api/v1/series/${id}`, tipoDoc);
    }

    /**
     * Exportar la definición completa de una serie como archivo JSON (FSD-005).
     * Devuelve la respuesta axios completa (data = Blob, headers con Content-Disposition).
     * @param {string} id - ID del tipo de documento
     * @returns {Promise<AxiosResponse>} respuesta con data (Blob) y headers
     */
    async exportTipoDocumento(id) {
        return this.apiClient.get(`/api/v1/series/${id}/export`, {
            responseType: 'blob',
        });
    }

    /**
     * Crear una import-session validada (FSD-006). Sube el archivo y recibe handle + preview.
     * El cliente axios de useApiClient acepta FormData con Content-Type multipart automático.
     * @param {File} file - archivo JSON seleccionado por el usuario
     * @returns {Promise} 201 con {handle, preview} o 400 con {level, errors}
     */
    async validateImportSerie(file) {
        const formData = new FormData();
        formData.append('file', file);
        return this.apiClient.post('/api/v1/series/import-sessions', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    /**
     * Confirmar el alta de una serie validada (FSD-006).
     * @param {string} handle
     * @param {{idOverride?:string, nameOverride?:string, parentOverride:string}} overrides
     */
    async commitImportSerie(handle, overrides) {
        return this.apiClient.post(
            `/api/v1/series/import-sessions/${encodeURIComponent(handle)}/commit`,
            overrides
        );
    }

    // Note: Delete functionality deferred to another phase per user request

    // ==================== METADATA SPECIFICATIONS ====================

    /**
     * Obtener especificación de metadata para un tipo de documento
     * @param {string} idTipoDoc - ID del tipo de documento
     * @returns {Promise} Response con especificación de metadata
     */
    async getSpec(idTipoDoc) {
        return this.apiClient.get(`/api/v1/series/${idTipoDoc}/metadata-specs`);
    }

    /**
     * Actualizar especificación de metadata para un tipo de documento
     * @param {string} idTipoDoc - ID del tipo de documento
     * @param {Object} especificacion - Especificación de metadata actualizada
     * @returns {Promise} Response con resultado de la actualización
     */
    async updateSpec(idTipoDoc, especificacion) {
        return this.apiClient.put(`/api/v1/series/${idTipoDoc}/metadata-specs`, especificacion);
    }

    /**
     * Obtener conteos de documentos para una serie
     * @param {string} idTipoDoc - ID de la serie
     * @returns {Promise} Response con conteos
     */
    async getSerieCounts(idTipoDoc) {
        return this.apiClient.get(`/api/v1/series/${idTipoDoc}/counts`);
    }

    /**
     * Obtiene las estadísticas (lista de series documentales) para la página de estadísticas.
     * Los conteos se obtienen de forma lazy por serie usando getSerieCounts.
     * @returns {Promise} Response con lista de series documentales
     */
    async getSeriesStats() {
        return this.apiClient.get('/api/v1/series/stats');
    }

    /**
     * Obtener conteos de documentos para una serie
     * @param {string} idTipoDoc - ID de la serie
     * @returns {Promise} Response con conteos
     */
    async getSerieTotalCounts(idTipoDoc) {
        return this.apiClient.get(`/api/v1/series/${idTipoDoc}/counts/total`);
    }

}

/**
 * Hook personalizado para usar el servicio de tipos de documentos
 * @returns {SeriesService} Instancia del servicio
 */
export const useSeriesService = () => {
    const apiClient = useApiClient();
    return new SeriesService(apiClient);
};

// Backward compatibility aliases
export const useDocumentTypeService = useSeriesService;
export const useMetadataService = useSeriesService;

export default SeriesService;
